import {observer} from 'mobx-react-lite';
import moment from 'moment';
import React from 'react';
import {
  EAutocompleteCategoryDefinitionProps,
  EAutocompleteOption,
} from 'webapp-lib/Components';
import {DATE_FORMAT} from 'webapp-lib/utils';

import {EFTagsAutocomplete} from '#/Autocomplete/ETagsAutocomplete';
import {EnumOptionInResponse} from '@lexian/api/documents-api';
import {AddressPlaceholderField} from '_/sharedComponents/CreateFromTemplateWizard/fill-placeholders/AddressPlaceholderField';
import {Placeholder} from '_/store/template/template.types';
import {notUndefined} from '_/utilities';
import {
  EFAutocomplete,
  EFDateInput,
  EFTextArea,
  EFTextInput,
  notRequiredRule,
  requiredRule,
  UseFormReturnValues,
} from '_/utilities/form';
import {firstOrUndefined} from '_/utilities/listUtility';
import {assertUnreachable} from '_/utilities/types';

export type PlaceholderFieldProps = {
  placeholder: Placeholder;
  form: UseFormReturnValues;
  autofocus: boolean;
};

// eslint-disable-next-line complexity
const UPlaceholderField = ({
  placeholder,
  form,
  autofocus,
}: PlaceholderFieldProps): React.ReactElement => {
  const rules = placeholder.optional ? notRequiredRule : requiredRule;

  switch (placeholder.type) {
    case 'TEXT':
      return (
        <EFTextInput
          autoFocus={autofocus}
          control={form.control}
          defaultValue={
            placeholder.value ??
            firstOrUndefined(placeholder.defaultValue)?.value ??
            ''
          }
          label={placeholder.label}
          name={placeholder.key}
          rules={rules}
          type='text'
        />
      );
    case 'TEXTAREA':
      return (
        <EFTextArea
          autoFocus={autofocus}
          control={form.control}
          defaultValue={
            placeholder.value ??
            firstOrUndefined(placeholder.defaultValue)?.value ??
            ''
          }
          label={placeholder.label}
          minRows={3}
          name={placeholder.key}
          rules={rules}
        />
      );
    case 'NUMBER':
      return (
        <EFTextInput
          autoFocus={autofocus}
          control={form.control}
          defaultValue={
            placeholder.value ??
            firstOrUndefined(placeholder.defaultValue)?.value ??
            ''
          }
          label={placeholder.label}
          name={placeholder.key}
          rules={rules}
          type='number'
        />
      );
    case 'EMAIL':
    case 'PHONE':
    case 'LINK':
      return (
        <EFAutocomplete
          autoFocus={autofocus}
          control={form.control}
          defaultValue={
            placeholder.value ??
            firstOrUndefined(placeholder.defaultValue)?.value
          }
          label={placeholder.label}
          name={placeholder.key}
          options={
            firstOrUndefined(placeholder.defaultValue)?.options?.map(
              (option) => ({
                value: option.entityId,
                displayText: option.displayName,
              })
            ) ?? []
          }
          rules={rules}
          allowCustomInput
        />
      );
    case 'ADDRESS':
      return (
        <AddressPlaceholderField
          autofocus={autofocus}
          form={form}
          placeholder={placeholder}
        />
      );
    case 'DATE': {
      const value =
        typeof placeholder.value === 'string' && placeholder.value
          ? placeholder.value
          : firstOrUndefined(placeholder.defaultValue)?.value;
      return (
        <EFDateInput
          autoFocus={autofocus}
          control={form.control}
          defaultValue={value ? moment(value).format(DATE_FORMAT) : undefined}
          label={placeholder.label}
          name={placeholder.key}
          rules={rules}
        />
      );
    }
    case 'USER_AND_ROLE':
    case 'USER_AND_CONTACT_ROLE':
    case 'BILLABLE_CATEGORY':
    case 'LOCALE':
    case 'ENUM':
      return placeholder.multiple ? (
        <EFTagsAutocomplete
          autoFocus={autofocus}
          categories={extractCategories(placeholder.enumOptions)}
          control={form.control}
          defaultValue={placeholder.value ?? placeholder.defaultValue ?? []}
          label={placeholder.label}
          name={placeholder.key}
          options={composeAutocompleteOptions(placeholder.enumOptions)}
          rules={rules}
        />
      ) : (
        <EFAutocomplete
          autoFocus={autofocus}
          categories={extractCategories(placeholder.enumOptions)}
          control={form.control}
          defaultValue={
            placeholder.value ??
            firstOrUndefined(placeholder.defaultValue)?.value ??
            firstOrUndefined(placeholder.enumOptions)?.value
          }
          label={placeholder.label}
          name={placeholder.key}
          options={composeAutocompleteOptions(placeholder.enumOptions)}
          rules={rules}
        />
      );
    case 'CONTACT':
    case 'COLLABORATOR':
    case 'INVOLVED_PARTY':
    case 'CASUS':
    case 'CLIENT':
    case 'USER':
    case 'GROUP':
    case 'OFFICE_LOCATION':
    case 'CLIENT_MONEY_BANK_ACCOUNT':
    case 'SPECIALITY_FIELD':
      return <></>;
    default:
      assertUnreachable(placeholder.type);
  }
};

export const PlaceholderField = observer(UPlaceholderField);

function composeAutocompleteOptions(
  enumOptions: EnumOptionInResponse[] | undefined
): EAutocompleteOption[] {
  if (!enumOptions) {
    return [];
  }

  return enumOptions.map(
    (option): EAutocompleteOption => ({
      displayText: option.displayName,
      value: option.value,
      categoryName: option.categoryName ?? undefined,
    })
  );
}

function extractCategories(
  enumOptions: EnumOptionInResponse[] | undefined
): EAutocompleteCategoryDefinitionProps[] | undefined {
  if (!enumOptions) {
    return undefined;
  }

  const categories = enumOptions
    .map(({categoryName}) => categoryName)
    .filter(notUndefined);

  if (categories.length === 0) {
    return undefined;
  }

  return [...new Set(categories)].map(
    (categoryName): EAutocompleteCategoryDefinitionProps => ({
      name: categoryName,
      mainLabel: categoryName,
    })
  );
}
